import React, { FC, useState } from 'react';
import { useTranslate, Button } from 'react-admin';
import makeStyles from '@material-ui/core/styles/makeStyles';
import SvgIcon from '@material-ui/core/SvgIcon';
import { terminateApplication } from '~/api/applications';
import { Grid } from '@material-ui/core';
import { FlashOffIcon } from '~/img';
import useCrudGetManyReference from '~/hooks/useCrudGetManyReference';
import onError from '~/errorsHandler';
import { Confirmation } from '~/components';
import { useMutation } from 'react-query';
import TextInput from '~/components/TextInput';
import Alert from '@material-ui/lab/Alert';

const resource = 'applications';

type Target = 'individuals' | 'businesses';

interface TerminateApplicationProps {
  id: string;
  target: Target;
  targetId: string;
  onClose: () => void;
}

const TerminateApplication: FC<TerminateApplicationProps> = (props) => {
  const { id, target, targetId, onClose } = props;

  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState<string>('');
  const [reasonError, setReasonError] = useState<string>();
  const translate = useTranslate();
  const t = (key: string): string => translate(`components.Applications.actions.${key}`);

  const crudGetManyReference = useCrudGetManyReference(resource, target, targetId);

  const { mutateAsync } = useMutation(
    ({ id, body }: { id: string; body?: { reason?: string } }) => terminateApplication(id, body),
    {
      onSuccess: () => {
        crudGetManyReference();
        onClose();
        setLoading(false);
        setReason('');
        setReasonError(undefined);
      },
      onError: (error) => {
        onError(error);
        setLoading(false);
      },
    }
  );

  const handleReasonChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setReasonError(undefined);
    setReason(target.value);
  };

  const handleTerminate = () => {
    if (!reason) {
      setReasonError(translate('ra.validation.required'));
      throw new Error();
    }
    setLoading(true);
    mutateAsync({ id, body: { reason } });
  };

  const classes = useStyles();

  return (
    <Confirmation
      onConfirm={handleTerminate}
      onClose={() => {
        setReason('');
        setReasonError(undefined);
      }}
      confirmButtonProps={{
        loading: loading,
      }}
      confirmationSettings={{
        variant: 'modal',
        modal: {
          heading: t('attention'),
          content: (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity='warning'>{t('terminateConfirm')}</Alert>
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  autoFocus
                  fullWidth
                  multiline
                  rows={3}
                  rowsMax={20}
                  label={t('reason')}
                  value={reason}
                  onChange={handleReasonChange}
                  required
                  error={!!reasonError}
                  helperText={reasonError}
                />
              </Grid>
            </Grid>
          ),
        },
      }}
    >
      <Button label={t('terminate')} className={classes.warningColor} disabled={loading}>
        <SvgIcon>
          <FlashOffIcon />
        </SvgIcon>
      </Button>
    </Confirmation>
  );
};

const useStyles = makeStyles((theme) => ({
  warningColor: {
    color: theme.palette.warning.main,
  },
}));

export default TerminateApplication;
